import type { CreditPackKind, PlanId, PlanLimits } from "./billing.schemas.ts";

// The plan numbers, with no schema behind them. This package declares `sideEffects: false`, so the
// website's pricing page imports them and the bundler leaves the contract's schemas and zod out of
// a visitor's download (ADR-034 point 2, S34.01). `billing.schemas.ts` and
// `billing-seats.schemas.ts` re-export every name.

export type PaidPlan = Exclude<PlanId, "free">;

/** The paid plans a person can choose today (D1320). Unlimited is not among them: it stays a plan
 * id, its limits and its price, for whoever is already on it or is put on it by staff. */
export const OFFERED_PLANS = ["starter", "growth", "enterprise"] as const;
export type OfferedPlan = (typeof OFFERED_PLANS)[number];
/** The plan a chooser marks (D1329): Starter, beside Free, whichever plans are shown. */
export const MOST_POPULAR_PLAN: OfferedPlan = "starter";

/** The plans shown and sold only while an organization's `more-plans` flag is on (D1378). The
 * contract names no flag, so a caller reads the flag and hands over what it says. */
export const MORE_PLANS: readonly OfferedPlan[] = ["growth", "enterprise"];

/** The plans an organization is shown and may buy: Starter always; Growth and Enterprise while
 * `more-plans` is on. A plan it is already on is always shown, so an organization on Enterprise
 * sees its plan with the flag off (D1378). */
export function plansShown(options: {
  readonly morePlans: boolean;
  readonly current?: PlanId | undefined;
}): readonly OfferedPlan[] {
  return OFFERED_PLANS.filter(
    (plan) => options.morePlans || !MORE_PLANS.includes(plan) || plan === options.current,
  );
}

/** Every plan's name, on sale or not. */
export const PLAN_NAMES: Readonly<Record<PlanId, string>> = {
  free: "Free",
  starter: "Starter",
  growth: "Growth",
  enterprise: "Enterprise",
  unlimited: "Unlimited",
};

/** The plan ladder: what each plan holds. */
export const PLAN_LIMITS: Readonly<Record<PlanId, PlanLimits>> = {
  free: {
    // ADR-025 §3 (D711): the free plan holds no allowance; a new person's credits come once, as
    // the welcome grant, after their email is verified.
    credits: 0,
    profilesPerAnalysis: 100,
    senderAccounts: 1,
    campaigns: 1,
    lists: 1,
    projects: 3,
    influencerFinderResults: 10,
    emailsPerMonth: 0,
    emailSequences: 3,
  },
  starter: {
    // Epic 43 (D1361): the price fell to 2_900, and these four with it; the rest stay.
    credits: 3_000,
    profilesPerAnalysis: 2_500,
    senderAccounts: 3,
    campaigns: 2,
    lists: 3,
    projects: 5,
    influencerFinderResults: 50,
    emailsPerMonth: 1_200,
    emailSequences: 5,
  },
  // D1377: Growth sits between them, at Starter's former numbers but for its credits, email
  // accounts and seats.
  growth: {
    credits: 12_000,
    profilesPerAnalysis: 2_500,
    senderAccounts: 8,
    campaigns: 6,
    lists: 3,
    projects: 5,
    influencerFinderResults: 50,
    emailsPerMonth: 4_000,
    emailSequences: 5,
  },
  enterprise: {
    // D1377: 150_000 credits a month at the same price; its other limits stay.
    credits: 150_000,
    profilesPerAnalysis: 10_000,
    senderAccounts: 30,
    campaigns: 12,
    lists: 6,
    projects: 10,
    influencerFinderResults: 250,
    emailsPerMonth: 10_000,
    emailSequences: 10,
  },
  unlimited: {
    credits: null,
    profilesPerAnalysis: null,
    senderAccounts: null,
    campaigns: null,
    lists: null,
    projects: 20,
    influencerFinderResults: 500,
    emailsPerMonth: null,
    emailSequences: null,
  },
};

export type PlanOffer = {
  readonly id: OfferedPlan;
  readonly name: string;
  readonly tagline: string;
  /** Euro cents per month, billed monthly. */
  readonly monthlyCents: number;
  readonly ribbon: string | null;
  readonly highlights: readonly string[];
  readonly includedTitle: string;
  readonly included: readonly string[];
};

/** Annual billing takes this percent off. */
export const ANNUAL_DISCOUNT_PERCENT = 10;

export const PLAN_OFFERS: readonly PlanOffer[] = [
  {
    id: "starter",
    name: "Starter",
    tagline: "Perfect for small businesses and freelancers",
    monthlyCents: 2_900,
    ribbon: "Most Popular",
    highlights: ["3,000 credits/month", "3 email accounts", "1,200 emails/month", "2 campaigns"],
    includedTitle: "What's included?",
    included: [
      "Automatic prospecting tool",
      "Advanced segmentation",
      "Data export",
      "Anti-ban protection",
      "Email sender",
      "Proven Templates",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    tagline: "For teams that reach more people every month",
    monthlyCents: 9_900,
    ribbon: null,
    highlights: ["12,000 credits/month", "8 email accounts", "4,000 emails/month", "6 campaigns"],
    includedTitle: "What's included?",
    included: [
      "Automatic prospecting tool",
      "Advanced segmentation",
      "Data export",
      "Anti-ban protection",
      "Email sender",
      "Proven Templates",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For growing teams with advanced needs",
    monthlyCents: 99_700,
    ribbon: null,
    highlights: ["150,000 credits/month", "30 email accounts", "10,000 emails/month", "12 campaigns"],
    includedTitle: "Everything in Starter, plus:",
    included: [
      "Auto Enrich",
      "AI Writing Assistant",
      "Community Access",
      "Followed users",
      "Email Verifier",
      "Warming up emails",
    ],
  },
];

/**
 * Seats: each plan bundles a number of seats in its price (null is unlimited,
 * the Unlimited plan's own name); a seat beyond that costs `extraCents` per
 * period. The free plan sells no extra seats at all: growing a small team past
 * its two included seats means upgrading. Free and Starter both include two
 * (D1374), so moving up from Free never bills a seat.
 */
export const PLAN_SEATS: Readonly<
  Record<PlanId, { readonly included: number | null; readonly extraCents: number }>
> = {
  free: { included: 2, extraCents: 0 },
  starter: { included: 2, extraCents: 900 },
  growth: { included: 5, extraCents: 900 },
  enterprise: { included: 20, extraCents: 1_900 },
  unlimited: { included: null, extraCents: 0 },
};

/** Credit packs, cheaper per credit as they grow. Euro cents. */
export const CREDIT_PACKS: Readonly<
  Record<
    CreditPackKind,
    ReadonlyArray<{ readonly credits: number; readonly cents: number; readonly recommended?: boolean }>
  >
> = {
  analysis: [
    { credits: 2_000, cents: 2_900 },
    { credits: 5_000, cents: 5_900 },
    { credits: 10_000, cents: 9_900, recommended: true },
    { credits: 25_000, cents: 19_900 },
    { credits: 50_000, cents: 34_900 },
  ],
  ai: [
    { credits: 1_000, cents: 900 },
    { credits: 5_000, cents: 3_900, recommended: true },
    { credits: 10_000, cents: 6_900 },
    { credits: 25_000, cents: 14_900 },
  ],
};

/** Unlimited is off sale (D1320), so no offer carries its price; a subscriber's view and a staff
 * price list still read it here. */
const UNLIMITED_MONTHLY_CENTS = 99_700;

const forPeriod = (monthly: number, period: "month" | "year"): number =>
  period === "month" ? monthly : Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT_PERCENT / 100));

export function planPriceCents(plan: PaidPlan, period: "month" | "year"): number {
  const monthly =
    plan === "unlimited"
      ? UNLIMITED_MONTHLY_CENTS
      : (PLAN_OFFERS.find((entry) => entry.id === plan)?.monthlyCents ?? 0);
  return forPeriod(monthly, period);
}

/** What Enterprise cost before it rose to 99_700 (D1321). A running subscription keeps the price it
 * started with, so a row older than the stored price, and a Stripe subscription linked by hand, are
 * read against this. */
const FORMER_ENTERPRISE_MONTHLY_CENTS = 29_700;

export function formerEnterprisePriceCents(period: "month" | "year"): number {
  return forPeriod(FORMER_ENTERPRISE_MONTHLY_CENTS, period);
}
